import api from '@/api/syllabus';

const POLL_MS = 1000;
const POLL_RETRY_MS = 3000;
const TERMINAL_JOB_STATUSES = ['done', 'failed', 'cancelled'];

// Backend error types (MoleculerError.type) -> the toast keys under app.syllabus.errors.
const UPLOAD_ERROR_CODES = {
  UPLOAD_TOO_LARGE: 'DOC_TOO_LARGE',
  UPLOAD_UNSUPPORTED_TYPE: 'UNSUPPORTED',
  UPLOAD_HEIC_UNSUPPORTED: 'HEIC',
  UPLOAD_MIXED_SELECTION: 'MIXED',
  UPLOAD_EMPTY_FILE: 'UNSUPPORTED',
  SYLLABUS_EXISTS: 'SYLLABUS_EXISTS'
};

// Parse job errorCode (BE-3095) -> the S 7.3 message keys. NO_STRUCTURE is "no table to
// split into chapters"; everything else (PARSE_FAILED, AI_PROVIDER_ERROR, STALE,
// SOURCE_MISSING…) is "could not process the file".
const PARSE_ERROR_CODES = {
  NO_STRUCTURE: 'NO_TABLE'
};

const parseErrorCode = job => PARSE_ERROR_CODES[job && job.errorCode] || 'PROCESSING_FAILED';

// Module-scope, not state: timer handles are not data and must not be reactive.
let pollTimer = null;
let pollingJobId = null;
const pendingLoads = {};

const stopPolling = () => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
  pollingJobId = null;
};

// What the upload card shows once the parse job stopped. The parser (BE-3095) reports a
// non-language document as a failed job with errorCode NON_LANGUAGE.
const terminalUploadStatus = (job) => {
  if (job.status === 'cancelled') return 'idle';
  if (job.status === 'failed' && job.errorCode === 'NON_LANGUAGE') return 'non_language';
  return job.status;
};

const uploadErrorCode = (error) => {
  const type = error && error.response && error.response.data && error.response.data.type;
  return UPLOAD_ERROR_CODES[type] || 'UPLOAD_FAILED';
};

// Loads the class's syllabus once per session; `{ classId, force }` re-fetches. Never rejects:
// a failed load leaves the class unmarked, so the entry-point CTAs stay hidden instead of
// inviting an upload the backend may refuse.
const loadClassSyllabus = ({ commit, state }, payload) => {
  const classId = typeof payload === 'string' ? payload : payload && payload.classId;
  const force = !!(payload && payload.force);
  if (!classId) return Promise.resolve(null);
  if (!force && state.loadedClasses[classId]) return Promise.resolve(state.byClass[classId] || null);
  if (pendingLoads[classId]) return pendingLoads[classId];
  pendingLoads[classId] = api.getClassSyllabus(classId)
    .then((syllabus) => {
      commit('SET_CLASS_SYLLABUS', { classId, syllabus: syllabus || null });
      return syllabus || null;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('syllabus: could not load the class syllabus', error);
      return null;
    })
    .then((result) => {
      delete pendingLoads[classId];
      return result;
    });
  return pendingLoads[classId];
};

// Polls one job until it stops, then reloads the class's syllabus. `{ jobId, classId }` when
// resuming a job the server still reports as running (tab reopened mid-read). The parse job
// ends as soon as the check card has everything it shows; the rows' content is read in the
// background afterwards (a separate job this screen does not wait for).
const pollSyllabusJob = ({ commit, state, dispatch }, payload) => {
  const jobId = typeof payload === 'string' ? payload : payload && payload.jobId;
  const classId = (payload && payload.classId) || state.upload.classId;
  if (!jobId) return Promise.resolve();
  if (pollingJobId === jobId) return Promise.resolve();
  stopPolling();
  pollingJobId = jobId;
  if (classId && state.upload.classId !== classId) commit('SET_UPLOAD', { classId, status: 'reading', jobId });
  const tick = () => api.getSyllabusJob(jobId)
    .then((job) => {
      if (pollingJobId !== jobId) return;
      commit('SET_UPLOAD', { jobId, job });
      if (!TERMINAL_JOB_STATUSES.includes(job.status)) {
        pollTimer = setTimeout(tick, POLL_MS);
        return;
      }
      stopPolling();
      // Some chapters stayed unread: the reading card offers the retry (the file is not needed
      // again); this is not the uploader's failure message (S 7.3).
      if (job.status === 'failed' && job.errorCode === 'LESSONS_FAILED') {
        commit('SET_UPLOAD', { status: 'lessons_failed', error: null });
        dispatch('loadClassSyllabus', { classId, force: true });
        return;
      }
      const status = terminalUploadStatus(job);
      commit('SET_UPLOAD', {
        status,
        error: status === 'failed' ? { code: parseErrorCode(job) } : null
      });
      dispatch('loadClassSyllabus', { classId, force: true });
    })
    .catch(() => {
      if (pollingJobId !== jobId) return;
      pollTimer = setTimeout(tick, POLL_RETRY_MS);
    });
  return tick();
};

const uploadSyllabus = ({ commit, dispatch, state }, {
  classId, files, forceLanguage, replace, displayName, photoOrigin
}) => {
  stopPolling();
  commit('SET_LAST_ERROR', null);
  commit('SET_CHECK_AGAIN', { classId, value: false });
  commit('SET_UPLOAD', {
    classId,
    status: 'uploading',
    jobId: null,
    syllabusId: null,
    job: null,
    error: null,
    fileLabel: displayName,
    files: Array.from(files || []),
    photoOrigin: !!photoOrigin
  });
  const fields = { forceLanguage: forceLanguage ? 'true' : undefined, replace: replace ? 'true' : undefined };
  return api.uploadSyllabus(classId, files, fields)
    .then(({ jobId, syllabusId }) => {
      // The teacher left the screen while the request was in flight: the server has the
      // syllabus, so just forget the cached "no syllabus" answer; no polling off-screen.
      if (state.upload.classId !== classId) {
        commit('INVALIDATE_CLASS_SYLLABUS', classId);
        return undefined;
      }
      commit('SET_UPLOAD', { status: 'reading', jobId, syllabusId });
      return dispatch('pollSyllabusJob', { jobId, classId });
    })
    .catch((error) => {
      if (state.upload.classId !== classId) return;
      commit('SET_UPLOAD', { status: 'failed', error: { code: uploadErrorCode(error) } });
    });
};

// The syllabus screen is leaving (other tab, other class, other page). Polling only runs
// while the reading card is visible: the timer stops, an in-flight upload/read is dropped
// from the store, and the class's cached answer is invalidated so the next visit asks the
// server, which either shows the finished syllabus or resumes polling via the
// ClassSyllabus `syllabus` watcher.
const leaveSyllabusScreen = ({ commit, state }, classId) => {
  stopPolling();
  if (!classId) return;
  if (state.upload.classId === classId) {
    if (['uploading', 'reading', 'lessons_failed'].includes(state.upload.status)) {
      commit('RESET_UPLOAD');
      commit('INVALIDATE_CLASS_SYLLABUS', classId);
    }
  }
};

const cancelSyllabusJob = ({ commit, state, dispatch }) => {
  const { jobId, classId } = state.upload;
  stopPolling();
  commit('RESET_UPLOAD');
  if (!jobId) return Promise.resolve();
  return api.cancelSyllabusJob(jobId)
    .catch(() => null)
    .then(() => dispatch('loadClassSyllabus', { classId, force: true }));
};

const resetUpload = ({ commit }) => {
  stopPolling();
  commit('RESET_UPLOAD');
};

// ---- the check step (XWEB-1203) ----
// Every call answers the refreshed summary, which simply replaces the cached syllabus.

const applySummary = (commit, classId, summary) => {
  if (summary) commit('SET_CLASS_SYLLABUS', { classId, syllabus: summary });
  return summary;
};

// "Igen, jól olvastuk be →"
const confirmReading = ({ commit }, { classId, syllabusId }) => api.confirmSyllabus(syllabusId)
  .then((summary) => {
    commit('SET_CHECK_AGAIN', { classId, value: false });
    return applySummary(commit, classId, summary);
  });

// "‹ Vissza az ellenőrzéshez" on the scope card: the confirmation stays on the server, the
// check card is shown again until the teacher confirms once more.
const backToCheck = ({ commit }, classId) => {
  commit('SET_CHECK_AGAIN', { classId, value: true });
};

// "Mennyit szeretne most átnézni?" -> 'next8' | 'semester' | 'year'
const setReviewScope = ({ commit }, { classId, syllabusId, scope }) => api.setSyllabusScope(syllabusId, scope)
  .then(summary => applySummary(commit, classId, summary));

// A syllabus without dates: the weekly hours picker (spec 7.2); the server recomputes the dates.
const setLessonsPerWeek = ({ commit }, { classId, syllabusId, lessonsPerWeek }) => (
  api.setSyllabusLessonsPerWeek(syllabusId, lessonsPerWeek)
    .then(summary => applySummary(commit, classId, summary))
);

// "Valami nem stimmel" -> 'edit' | 'reupload'. Measurement only, so a failure is ignored.
const reportIssue = (context, { syllabusId, choice }) => api.reportSyllabusIssue(syllabusId, choice)
  .catch(() => null);

// "Másik fájlt töltök fel" from the check card: the same upload, replacing the live syllabus.
const replaceSyllabus = (context, payload) => uploadSyllabus(context, { ...payload, replace: true });

// Drops the class's live syllabus (archived on the server); the tab falls back to the uploader.
const archiveSyllabus = ({ commit }, { classId, syllabusId }) => api.archiveSyllabus(syllabusId)
  .then(() => {
    commit('SET_CLASS_SYLLABUS', { classId, syllabus: null });
    commit('SET_CHECK_AGAIN', { classId, value: false });
    return true;
  });

// "Újrapróbálás" on the reading card: the chapters that could not be read are asked again (no
// new upload); the reading card runs again until the check card can open.
const retryLessons = ({ commit, dispatch }, { classId, syllabusId }) => api.retrySyllabusLessons(syllabusId)
  .then((summary) => {
    commit('COUNT_LESSON_RETRY', syllabusId);
    applySummary(commit, classId, summary);
    const job = summary && summary.job;
    if (!job) return summary;
    commit('SET_UPLOAD', { classId, status: 'reading', jobId: job.jobId, syllabusId, job, error: null });
    return dispatch('pollSyllabusJob', { jobId: job.jobId, classId }).then(() => summary);
  });

// What the uploader card shows after a failed read (S 7.3), kept until the next pick.
const rememberError = ({ commit }, { classId, code }) => {
  commit('SET_LAST_ERROR', code ? { classId, code } : null);
};

export default {
  loadClassSyllabus,
  pollSyllabusJob,
  uploadSyllabus,
  cancelSyllabusJob,
  resetUpload,
  leaveSyllabusScreen,
  confirmReading,
  backToCheck,
  setReviewScope,
  setLessonsPerWeek,
  reportIssue,
  replaceSyllabus,
  archiveSyllabus,
  retryLessons,
  rememberError
};



// WEBPACK FOOTER //
// ./src/store/modules/syllabus/actions.js